import { useForm, Controller } from 'react-hook-form';
import { AccountsService } from '../../../services/accounts.service';
import LogData from '../../../components/logs/log-data.component';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertService } from '../../../services/alert.service';
import { Dropzone, ExtFile } from '@dropzone-ui/react';
import { CompaniesService } from '../../../services/companies.service';
import { BlackListService } from '../../../services/blacklist.service';
import Tooltip from '../../../components/forms/tooltip.component';
import Input from '../../../components/forms/input.component';
import Switch from '../../../components/forms/switch.component';
import Select from 'react-select';
import Button from '../../../components/forms/button.component';
import Title from '../../../components/forms/titles.component';
import InvalidFeedback from '../../../components/forms/invalid-feedback.component';
import Icon from '../../../components/forms/icon.component';
import Navigate from '../../../components/cards/header-navigate.component';
import { useSession } from '../../../hooks/use-session.hook';
import { PROFILES } from '../../../constants';

interface AddBlackListProps {
  setPage: (page: string) => void;
}

interface FormValues {
  file: any;
  name: string;
  companies: string;
  account: string;
  priority: boolean;
}

function AddBlackList({ setPage }: AddBlackListProps) {
  const { t } = useTranslation();
  const { session, selectedAccount } = useSession();
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [account, setAccount] = useState<any[]>([]);
  const defaultValues: FormValues = {
    file: '',
    name: '',
    companies: '',
    priority: false,
    account: ''
  };

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isValid, isSubmitting }
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues
  });

  const file = watch('file');

  const updateFiles = (incomingFiles: ExtFile[]) => {
    const allowedExtensions = ['.csv'];
    const file = incomingFiles[0]?.file as File | null;

    if (file && allowedExtensions.includes(`.${file.name.split('.').pop()}`)) {
      setValue('file', file as any);
    } else {
      AlertService.toastError(t('blacklist-page.file-error'));
      setValue('file', '');
    }
  };

  const removeFile = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.stopPropagation();
    setValue('file', '');
  };

  const getCompanies = async () => {
    try {
      const data = await CompaniesService.getCompanies();
      setCompanies(
        data.map((item: any) => ({
          value: item._id,
          label: item.name,
          type: item.service_type
        }))
      );
    } catch (errorCode: any) {
      console.error('Error obteniendo empresas:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
      setCompanies([]);
    }
  };

  const getAccount = async () => {
    if (selectedCompany) {
      try {
        const data = await AccountsService.getAccountsForCompany(selectedCompany);
        setAccount(
          data.map((item: any) => ({
            value: item._id,
            label: item.name,
            type: item.service_type
          }))
        );
      } catch (errorCode: any) {
        AlertService.toastError(errorCode.error || errorCode);
        console.error('Error obteniendo empresas:', errorCode);
        setAccount([]);
      }
    }
  };

  const handleDownloadInvalidNumbers = (numbers: string[]) => () => {
    if (!numbers || numbers.length === 0) return;

    const csvContent = numbers.map((num: string) => `"${num}"`).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'numeros_invalidos.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const submit = async (data: FormValues, buttonId: string) => {
    if (!file) {
      AlertService.toastWarning(t('blacklist-page.warning-csv-file'));
      return;
    }
    try {
      const confirmed = await AlertService.question(
        t('blacklist-page.upload-confirm'),
        t('blacklist-page.upload-confirm-description'),
        () => Promise.resolve(),
        t('general.upload')
      );

      if (!confirmed) return;
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('account', data.account);
      formData.append('priority', data.priority.toString());
      formData.append('csv', data.file);

      const response = await BlackListService.saveBlackList(formData);

      const html = (
        <div className=" text-start">
          <h2 className="fs-2 fw-bold mb-4">Resumen:</h2>

          <div className="d-flex flex-column gap-2 fs-5">
            <p className="mb-0">
              Números agregados:{' '}
              <strong className="text-info fw-bolder fs-4">{response.totalNumbers}</strong>
            </p>
            {response.invalidNumbers && response.invalidNumbers.length > 0 && (
              <p className="mb-0">
                Números inválidos:{' '}
                <strong className="text-danger fw-bolder">{response.invalidNumbers.length}</strong>
                <a
                  onClick={handleDownloadInvalidNumbers(response.invalidNumbers)}
                  className="ms-2 text-decoration-underline text-danger fw-bolder cursor-pointer"
                >
                  Ver números
                </a>
              </p>
            )}
          </div>
        </div>
      );

      AlertService.success(<p className="h2">¡ Lista negra agregada correctamente !</p>, html);

      reset(defaultValues);
      LogData(data, buttonId, window.location.href);
      setPage('principal');
    } catch (errorCode: any) {
      console.log('Error: ', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  useEffect(() => {
    getCompanies();
  }, []);

  useEffect(() => {
    if (session.profile === PROFILES.ADMIN) {
      setValue('account', selectedAccount.account_id);
    }
  }, [selectedAccount, session]);

  useEffect(() => {
    getAccount();
  }, [selectedCompany]);

  return (
    <>
      <Navigate
        title={t('roles.btn-add-modal')}
        titleBolder={t('asaid.link-blacklist')}
        subtitle={t('blacklist-page.page-text-add')}
        breadcrumbItems={[t('asaid.link-blacklist'), t('general.register')]}
        btnBack
      />
      <form onSubmit={handleSubmit((data) => submit(data, 'addBlackList-7836457891'))}>
        <div className="row gy-5 g-xl-10 stepper" id="add_new_area_stepper">
          <div className="col-xl-5 mb-xl-10">
            <div className="card mb-5 mb-xxl-8">
              <div className="card-header align-items-center border-0 mt-5 mb-1">
                <div className="mt-6">
                  <Title
                    icon="ki-file-up"
                    title={t('blacklist-page.file')}
                    subtitle={t('blacklist-page.add-numbers')}
                    type="info"
                  />
                </div>
              </div>
              <div className="card-body pt-1">
                <div className="col-12 mb-10 mb-lg-0 h-100">
                  {!file ? (
                    <Dropzone
                      onChange={updateFiles}
                      className="dropzone cursor-default"
                      accept=".csv"
                      maxFiles={1}
                    >
                      <div className="dz-mkssage">
                        <>
                          <i className="ki-duotone ki-file-up fs-5x text-primary me-4">
                            <span className="path1" />
                            <span className="path2" />
                          </i>
                          <div className="d-flex flex-center flex-grow-1 flex-wrap flex-md-nowrap">
                            <div className="mb-3 mb-md-0 fw-semibold">
                              <h5 className="text-gray-900 fw-bold">
                                {t('campaigns.upload-file')}
                              </h5>
                              <div className="fs-7 pe-7 text-info">
                                {t('blacklist-page.upload-file-description')}
                              </div>
                            </div>
                          </div>
                        </>
                      </div>
                    </Dropzone>
                  ) : (
                    <div className="dropzone cursor-default">
                      <div className="dz-g">
                        <i className="ki-duotone ki-file-added text-primary fs-5x text-primary me-4">
                          <span className="path1" />
                          <span className="path2" />
                        </i>
                        <div className="d-flex flex-column align-items-center flex-grow-1">
                          <div className="mb-3 mb-md-0 fw-semibold text-center">
                            <h5 className="text-gray-900 fw-bold">
                              {t('blacklist-page.file-uploaded')}
                            </h5>
                            <div className="fs-7 text-info">
                              {typeof file === 'object' && file !== null && 'name' in file
                                ? (file as File).name
                                : t('blacklist-page.no-file-uploaded')}
                            </div>
                          </div>
                          <a
                            className="btn btn-danger px-6 text-nowrap btn-sm mt-3"
                            href="#"
                            onClick={removeFile}
                          >
                            {t('general.delete')}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="d-flex align-items-start gap-4 mb-5 mt-10">
                  <span className="symbol me-3 symbol-label"></span>
                  <div className="d-flex flex-column">
                    <span className="fs-3 mb-2">
                      Ejemplo de archivo <strong className="text-info fw-bolder">CSV:</strong>
                    </span>
                    <img
                      src="/assets/media/examples/blacklist_example.png"
                      alt="Ejemplo de CSV"
                      className="img-fluid rounded-12 border"
                      width={'80%'}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-7 mb-5 mb-xl-10">
            <div className="card mb-xxl-8s">
              <div className="card-header align-items-center border-0 mt-10">
                <div className="mt-0">
                  <Title
                    icon="ki-notepad-edit"
                    title={t('blacklist-page.data-general')}
                    subtitle={t('blacklist-page.data-general-description')}
                    type="info"
                  />
                </div>
              </div>
              <div className="card-body">
                <div className="flex-column current" data-kt-stepper-element="content">
                  <div className="pb-8 pb-lg-10">
                    {session.profile === PROFILES.ROOT && (
                      <>
                        <div className="fv-row mb-7">
                          <label className="form-label">
                            {t('blacklist-page.company-label')}
                            <span className="fs-8 text-muted ms-1 required"></span>
                          </label>
                          <Controller
                            control={control}
                            name="companies"
                            rules={{ required: true }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                              <>
                                <Select
                                  options={companies}
                                  noOptionsMessage={({ inputValue }) =>
                                    inputValue
                                      ? t('general.no-options', { inputValue })
                                      : t('general.no-options')
                                  }
                                  className={
                                    error ? 'form-select-custom is-invalid' : 'form-select-custom'
                                  }
                                  classNamePrefix="form-select-custom"
                                  placeholder={t('blacklist-page.company-placeholder')}
                                  onChange={(selectedOption) => {
                                    const selectedValue = selectedOption
                                      ? selectedOption.value
                                      : '';
                                    setSelectedCompany(selectedValue);
                                    onChange(selectedValue);
                                  }}
                                  value={
                                    companies.filter((option: any) =>
                                      value?.includes(option.value)
                                    ) || []
                                  }
                                />
                                <InvalidFeedback error={error} />
                              </>
                            )}
                          />
                        </div>
                        <div className="fv-row mb-7">
                          <label className="form-label">{t('blacklist-page.account-label')}</label>
                          <span className="fs-8 text-primary d-block mt-0 mb-5 fw-bolder">
                            {t('blacklist-page.text-input')}
                          </span>
                          <Controller
                            control={control}
                            name="account"
                            rules={{ required: false }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                              <>
                                <Select
                                  options={account}
                                  isClearable
                                  noOptionsMessage={({ inputValue }) =>
                                    inputValue
                                      ? t('general.no-options', { inputValue })
                                      : t('general.no-options')
                                  }
                                  className={
                                    error ? 'form-select-custom is-invalid' : 'form-select-custom'
                                  }
                                  classNamePrefix="form-select-custom"
                                  placeholder={t('blacklist-page.account-placeholder')}
                                  onChange={(selectedOption) => {
                                    onChange(selectedOption?.value);
                                  }}
                                  value={
                                    account.find((option: any) => option.value === value) || null
                                  }
                                />
                                <InvalidFeedback error={error} />
                              </>
                            )}
                          />
                        </div>
                      </>
                    )}
                    <div className="fv-row mb-10">
                      <Input
                        label={t('blacklist-page.name')}
                        requiredIndicator="required"
                        placeholder={t('blacklist-page.name-placeholder')}
                        errors={errors?.name}
                        {...register('name', {
                          required: true,
                          setValueAs: (value) => value.trim()
                        })}
                      />
                    </div>
                    <div className="separator separator-dashed text-dark mb-5"></div>
                    <div className="fv-row mb-1">
                      <div className="d-flex flex-stack cursor-pointer mb-3">
                        <div className="d-flex align-items-center mb-5">
                          <span className="symbol me-e6 symbol-label"></span>
                          <div className="d-flex flex-column">
                            <span className="fw-bolder fs-3">
                              Lista negra prioritaria{' '}
                              <Tooltip
                                title="Al activar esta opción, esta lista negra se seleccionará
                                       automáticamente al crear tus campañas. Esto garantiza que los números
                                       asignados a esta lista nunca reciban un mensaje al enviar tus
                                       campañas."
                                className="d-inline-block cursor-pointer"
                              >
                                <button
                                  className="btn btn-icon btn-transparent pulse pulse-info"
                                  type="button"
                                >
                                  <Icon
                                    icon="ki-information-3"
                                    size="fs-1"
                                    className="fw-bolder me-2"
                                    type="info"
                                  />
                                  <span className="pulse-ring me-2" />
                                </button>
                              </Tooltip>
                            </span>
                          </div>
                        </div>
                        <span className="form-check form-check-custom form-check-solid">
                          <Switch {...register('priority')} />
                        </span>
                      </div>
                    </div>
                    <div className="separator separator-dashed text-dark mb-10"></div>
                    <div className="d-flex justify-content-end">
                      <Button
                        type="submit"
                        className="btn btn-primary hover-scale fw-bolder"
                        disabled={!isValid || !file}
                        isLoading={isSubmitting}
                      >
                        {t('general.save')} <Icon icon="ki-send" size="fs-1" ClassName="ms-2" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default AddBlackList;
